import { useEffect, useMemo, useState } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import GameAppCard from '@components/main/GameAppCard'
import SortableFavoriteGameCard from '@components/main/SortableFavoriteGameCard'
import { type GameApp } from '@app-types/api/auth'
import { useAuthStore } from '@stores/authStore'
import { useGameFavoritesStore } from '@stores/gameFavoritesStore'

const emptyApps: GameApp[] = []

function MainContent() {
  const session = useAuthStore((state) => state.session)
  const apps = session?.apps ?? emptyApps
  const error = useAuthStore((state) => state.error)
  const isLoading = useAuthStore((state) => state.isLoading)
  const favoriteAppIds = useGameFavoritesStore((state) => state.favoriteAppIds)
  const loadFavorites = useGameFavoritesStore((state) => state.loadFavorites)
  const reorderFavorite = useGameFavoritesStore(
    (state) => state.reorderFavorite,
  )
  const resetFavorites = useGameFavoritesStore((state) => state.resetFavorites)
  const toggleFavorite = useGameFavoritesStore((state) => state.toggleFavorite)
  const [search, setSearch] = useState('')
  const userId = session?.user?.id ?? null
  const csrfToken = session?.csrfToken ?? null
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  useEffect(() => {
    if (userId === null || csrfToken === null) {
      resetFavorites()
      return
    }

    void loadFavorites(userId, csrfToken)
  }, [csrfToken, loadFavorites, resetFavorites, userId])

  const filteredApps = useMemo(() => {
    const query = search.trim().toLowerCase()

    if (!query) {
      return apps
    }

    return apps.filter((app) => {
      return (
        app.name.toLowerCase().includes(query) ||
        app.slug.toLowerCase().includes(query)
      )
    })
  }, [apps, search])
  const favoriteApps = useMemo(() => {
    const appsById = new Map(filteredApps.map((app) => [app.id, app]))

    return favoriteAppIds
      .map((appId) => appsById.get(appId))
      .filter((app): app is GameApp => Boolean(app))
  }, [favoriteAppIds, filteredApps])
  const otherApps = useMemo(() => {
    const favoriteIds = new Set(favoriteAppIds)

    return filteredApps.filter((app) => !favoriteIds.has(app.id))
  }, [favoriteAppIds, filteredApps])

  function onFavoriteDragEnd(event: DragEndEvent) {
    const sourceAppId = Number(event.active.id)
    const targetAppId = Number(event.over?.id)

    if (
      Number.isInteger(sourceAppId) &&
      Number.isInteger(targetAppId) &&
      sourceAppId !== targetAppId
    ) {
      reorderFavorite(sourceAppId, targetAppId)
    }
  }

  return (
    <section className="min-h-full min-w-0 overflow-y-auto bg-slate-50 p-6 dark:bg-zinc-950 max-[820px]:overflow-visible max-[820px]:p-4">
      <div className="mx-auto w-full max-w-6xl">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="m-0 text-2xl font-semibold tracking-normal text-slate-950 dark:text-slate-100">
              Игры
            </h1>
            {apps.length > 0 ? (
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {filteredApps.length}/{apps.length}
              </p>
            ) : null}
          </div>

          <label className="w-full max-w-sm">
            <span className="sr-only">Поиск по играм</span>
            <input
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-3 focus:ring-violet-500/15 dark:border-zinc-700 dark:bg-zinc-950 dark:text-slate-100 dark:placeholder:text-zinc-500"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск игры"
            />
          </label>
        </div>

        {error ? (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
            {error}
          </div>
        ) : null}

        {isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="h-20 animate-pulse rounded-lg bg-white dark:bg-zinc-900" />
            <div className="h-20 animate-pulse rounded-lg bg-white dark:bg-zinc-900" />
            <div className="h-20 animate-pulse rounded-lg bg-white dark:bg-zinc-900" />
            <div className="h-20 animate-pulse rounded-lg bg-white dark:bg-zinc-900" />
          </div>
        ) : null}

        {!isLoading && filteredApps.length > 0 ? (
          <div className="grid gap-4">
            {favoriteApps.length > 0 ? (
              <DndContext sensors={sensors} onDragEnd={onFavoriteDragEnd}>
                <SortableContext
                  items={favoriteApps.map((app) => app.id)}
                  strategy={rectSortingStrategy}
                >
                  <ul className="m-0 grid list-none gap-3 px-0 py-0 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {favoriteApps.map((app) => (
                      <SortableFavoriteGameCard
                        app={app}
                        key={app.id}
                        onToggleFavorite={toggleFavorite}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            ) : null}

            {favoriteApps.length > 0 && otherApps.length > 0 ? (
              <div className="border-t border-slate-200 dark:border-zinc-800" />
            ) : null}

            {otherApps.length > 0 ? (
              <ul className="m-0 grid list-none gap-3 px-0 py-0 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {otherApps.map((app) => (
                  <li key={app.id}>
                    <GameAppCard
                      app={app}
                      isFavorite={false}
                      onToggleFavorite={toggleFavorite}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {!isLoading && filteredApps.length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-white px-4 py-4 text-sm text-slate-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-slate-400">
            Игры не найдены
          </div>
        ) : null}
      </div>
    </section>
  )
}

export default MainContent
